import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { fetchInquiries, fetchStats, updateInquiryStatus, deleteInquiry, adminLogout } from "../lib/api";
import { LogOut, RefreshCw, Trash2, KeyRound } from "lucide-react";
import { ChangePasswordModal } from "../components/ChangePasswordModal";

const STATUS_OPTIONS = ["new", "contacted", "closed"];
const TAG_OPTIONS = ["fitout", "renovation", "curtains"];
const TAG_LABEL = { fitout: "Fit-Out", renovation: "Renovation", curtains: "Curtains" };

export default function AdminDashboard() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [filter, setFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showChangePw, setShowChangePw] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inqRes, sRes] = await Promise.all([fetchInquiries(), fetchStats()]);
      setItems(inqRes.data);
      setStats(sRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("tf_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Failed to load inquiries");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = async () => {
    try { await adminLogout(); } catch { /* cookie may already be gone */ }
    localStorage.removeItem("tf_admin_token");
    navigate("/admin/login");
  };

  const onPasswordChanged = () => {
    // Backend already cleared the CMS cookie and revoked prior tokens.
    setShowChangePw(false);
    try {
      localStorage.removeItem("tf_admin_token");
      localStorage.removeItem("tf_admin_email");
    } catch { /* storage disabled */ }
    navigate("/admin/login");
  };

  const changeStatus = async (id, status) => {
    try {
      await updateInquiryStatus(id, status);
      toast.success("Status updated");
      setSelected((s) => (s && s.id === id ? { ...s, status } : s));
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const removeInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try {
      await deleteInquiry(id);
      toast.success("Deleted");
      setSelected(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = items.filter(
    (i) =>
      (filter === "all" || i.status === filter) &&
      (tagFilter === "all" || i.service_tag === tagFilter)
  );

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAF5EC] text-[#1C110C]">
      <Toaster theme="light" position="top-center" />

      {/* Top bar */}
      <div className="border-b border-[#6E1F2C]/15 bg-[#FAF5EC]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <p className="font-serif-display tracking-[0.32em] uppercase text-sm">
            Time Furniture <span className="text-[#6E1F2C]">·</span> Admin
          </p>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#7A6B5F] hidden md:block">
              {localStorage.getItem("tf_admin_email")}
            </span>
            <button
              onClick={() => navigate("/admin/crm")}
              data-testid="admin-crm-link"
              className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase bg-[#6E1F2C] text-[#FAF5EC] px-4 py-2 hover:bg-[#56151F]"
            >
              AI Sales CRM
            </button>
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="text-[#7A6B5F] hover:text-[#6E1F2C] p-2"
              aria-label="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={() => setShowChangePw(true)}
              data-testid="admin-change-password-open"
              className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase border border-[#6E1F2C]/25 px-4 py-2 hover:border-[#6E1F2C]"
            >
              <KeyRound size={12} /> <span className="hidden sm:inline">Change Password</span>
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase border border-[#6E1F2C]/25 px-4 py-2 hover:border-[#6E1F2C]"
            >
              <LogOut size={12} /> <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      {showChangePw && (
        <ChangePasswordModal onClose={() => setShowChangePw(false)} onSuccess={onPasswordChanged} />
      )}

      {/* Stats */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#6E1F2C]/10 border border-[#6E1F2C]/15">
          {[
            { k: "total", label: "All Inquiries" },
            { k: "new", label: "New" },
            { k: "contacted", label: "Contacted" },
            { k: "closed", label: "Closed" },
          ].map((s) => (
            <div key={s.k} data-testid={`admin-stat-${s.k}`} className="bg-[#FAF5EC] p-6">
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F]">{s.label}</p>
              <p className="font-serif-display text-4xl mt-2 text-[#1C110C]">{stats[s.k]}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-8 pb-2 flex gap-2 flex-wrap items-center">
        <span className="text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mr-2">Status</span>
        {["all", ...STATUS_OPTIONS].map((f) => (
          <button
            key={f}
            data-testid={`admin-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 text-[10px] tracking-[0.28em] uppercase border transition ${
              filter === f
                ? "bg-[#6E1F2C] text-[#FAF5EC] border-[#6E1F2C]"
                : "border-[#6E1F2C]/25 text-[#7A6B5F] hover:text-[#1C110C]"
            }`}
          >
            {f}
          </button>
        ))}
      </div>
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pb-4 flex gap-2 flex-wrap items-center">
        <span className="text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mr-2">Tag</span>
        {["all", ...TAG_OPTIONS].map((t) => (
          <button
            key={t}
            data-testid={`admin-tag-filter-${t}`}
            onClick={() => setTagFilter(t)}
            className={`px-4 py-2 text-[10px] tracking-[0.28em] uppercase border transition ${
              tagFilter === t
                ? "bg-[#D4A963] text-[#1C110C] border-[#D4A963]"
                : "border-[#6E1F2C]/25 text-[#7A6B5F] hover:text-[#1C110C]"
            }`}
          >
            {t === "all" ? "all" : TAG_LABEL[t]}
          </button>
        ))}
      </div>

      {/* Grid: Table + Detail */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 border border-[#6E1F2C]/15 overflow-hidden">
          <div className="grid grid-cols-[1.1fr_1.3fr_0.9fr_0.7fr_0.7fr_0.6fr] text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] border-b border-[#6E1F2C]/15 bg-white">
            <div className="px-5 py-4">Name</div>
            <div className="px-5 py-4">Email</div>
            <div className="px-5 py-4">Service</div>
            <div className="px-5 py-4">Tag</div>
            <div className="px-5 py-4">Date</div>
            <div className="px-5 py-4">Status</div>
          </div>
          {loading ? (
            <p className="text-center text-[#7A6B5F] py-16">Loading...</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-[#7A6B5F] py-16" data-testid="admin-empty-state">
              No inquiries yet.
            </p>
          ) : (
            filtered.map((i) => (
              <button
                key={i.id}
                data-testid={`admin-row-${i.id}`}
                onClick={() => setSelected(i)}
                className={`w-full grid grid-cols-[1.1fr_1.3fr_0.9fr_0.7fr_0.7fr_0.6fr] text-left text-sm border-b border-[#6E1F2C]/10 hover:bg-[#6E1F2C]/5 transition ${
                  selected?.id === i.id ? "bg-[#6E1F2C]/5" : ""
                }`}
              >
                <div className="px-5 py-4 text-[#1C110C]">{i.name}</div>
                <div className="px-5 py-4 text-[#7A6B5F] truncate">{i.email}</div>
                <div className="px-5 py-4 text-[#7A6B5F] truncate">{i.service || "—"}</div>
                <div className="px-5 py-4">
                  {i.service_tag ? (
                    <span
                      data-testid={`admin-tag-badge-${i.service_tag}`}
                      className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 ${tagClass(i.service_tag)}`}
                    >
                      {TAG_LABEL[i.service_tag]}
                    </span>
                  ) : (
                    <span className="text-[#7A6B5F] text-xs">—</span>
                  )}
                </div>
                <div className="px-5 py-4 text-[#7A6B5F] text-xs">
                  {new Date(i.created_at).toLocaleDateString()}
                </div>
                <div className="px-5 py-4">
                  <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 ${statusClass(i.status)}`}>
                    {i.status}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Detail pane */}
        <div className="lg:col-span-5 border border-[#6E1F2C]/15 p-6 bg-white min-h-[400px]" data-testid="admin-detail">
          {!selected ? (
            <p className="text-center text-[#7A6B5F] py-16 text-sm">Select an inquiry to view details.</p>
          ) : (
            <div>
              <p className="eyebrow mb-3">Inquiry Detail</p>
              <h2 className="font-serif-display text-3xl mb-2">{selected.name}</h2>
              <p className="text-sm text-[#7A6B5F]">
                {new Date(selected.created_at).toLocaleString()}
              </p>

              <dl className="mt-6 space-y-3 text-sm border-t border-[#6E1F2C]/15 pt-6">
                <Detail label="Email" value={<a className="link-elegant" href={`mailto:${selected.email}`}>{selected.email}</a>} />
                <Detail label="Phone" value={selected.phone || "—"} />
                <Detail label="Service" value={selected.service || "—"} />
                <Detail
                  label="Tag"
                  value={
                    selected.service_tag ? (
                      <span
                        className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 ${tagClass(selected.service_tag)}`}
                      >
                        {TAG_LABEL[selected.service_tag]}
                      </span>
                    ) : (
                      "—"
                    )
                  }
                />
                {selected.package_tier && <Detail label="Package" value={selected.package_tier} />}
                {selected.property_size && <Detail label="Property" value={selected.property_size} />}
                {selected.location && <Detail label="Location" value={selected.location} />}
                <Detail label="Email sent" value={selected.email_sent ? "Yes" : "No"} />
              </dl>

              <div className="mt-6 border-t border-[#6E1F2C]/15 pt-6">
                <p className="eyebrow mb-3">Message</p>
                <p className="text-sm text-[#1C110C] whitespace-pre-wrap leading-relaxed">{selected.message}</p>
              </div>

              <div className="mt-8 border-t border-[#6E1F2C]/15 pt-6">
                <p className="eyebrow mb-3">Status</p>
                <div className="flex gap-2 flex-wrap">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid={`admin-set-${s}-${selected.id}`}
                      onClick={() => changeStatus(selected.id, s)}
                      className={`text-[10px] tracking-[0.28em] uppercase px-4 py-2 border transition ${
                        selected.status === s
                          ? "bg-[#6E1F2C] text-[#FAF5EC] border-[#B8904E]"
                          : "border-[#6E1F2C]/25 text-[#7A6B5F] hover:text-[#1C110C]"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => removeInquiry(selected.id)}
                data-testid={`admin-delete-${selected.id}`}
                className="mt-8 inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase border border-red-900/60 text-red-300/80 px-4 py-2 hover:bg-red-900/20"
              >
                <Trash2 size={12} /> Delete Inquiry
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function statusClass(s) {
  if (s === "new") return "bg-[#6E1F2C] text-[#FAF5EC]";
  if (s === "contacted") return "bg-[#6E1F2C]/10 text-[#1C110C]";
  return "bg-transparent text-[#7A6B5F] border border-[#6E1F2C]/25";
}

function tagClass(t) {
  if (t === "fitout") return "bg-[#6E1F2C] text-[#FAF5EC]";
  if (t === "renovation") return "bg-[#1C110C] text-[#FAF5EC]";
  if (t === "curtains") return "bg-[#D4A963] text-[#1C110C]";
  return "bg-transparent text-[#7A6B5F] border border-[#6E1F2C]/25";
}

function Detail({ label, value }) {
  return (
    <div className="flex gap-4">
      <dt className="w-24 text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] pt-1">{label}</dt>
      <dd className="flex-1 text-[#1C110C]">{value}</dd>
    </div>
  );
}
